import type {
  ActivityRecord,
  ApiKey,
  ApiKeyScope,
  AuthMe,
  Bot,
  ChannelScopeStatus,
  ChannelSubscriptions,
  ChannelViewer,
  CreatedApiKey,
  CreatedEnrollmentLink,
  EnrollmentLink,
  EnrollmentScopePreview,
  FeatureBinding,
  HelixEndpoint,
  ScopeCatalogEntry,
  StoredChannel,
  SubscriptionType,
} from '../domain/types';

export const API_BASE = '/api/v2';
export const AUTH_BASE = '/auth';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code = 'error',
    readonly fields?: unknown,
    readonly scopes?: string[],
    readonly enrollmentHint?: boolean,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

export function authDisabled(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return String(error);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined) headers.set('content-type', 'application/json');
  const response = await fetch(path, { ...init, credentials: 'include', headers });
  const text = await response.text();
  const body: unknown = text ? safeJson(text) : undefined;
  if (!response.ok) {
    const detail = body as {
      error?: string;
      message?: string;
      fields?: unknown;
      scopes?: string[];
      enrollmentHint?: boolean;
    } | undefined;
    throw new ApiError(
      detail?.message || detail?.error || `${response.status} ${response.statusText}`,
      response.status,
      detail?.error ?? 'error',
      detail?.fields,
      detail?.scopes,
      detail?.enrollmentHint,
    );
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function json(method: string, body: unknown): RequestInit {
  return { method, body: JSON.stringify(body ?? {}) };
}

const segment = encodeURIComponent;

export const api = {
  me: () => request<AuthMe>(`${AUTH_BASE}/me`),
  logout: () => request<void>(`${AUTH_BASE}/logout`, { method: 'POST' }),
  loginUrl: `${AUTH_BASE}/theblob/login`,
  botOauthUrl: () => `${AUTH_BASE}/twitch/bot`,
  botReauthOauthUrl: (botId: string) => `${AUTH_BASE}/twitch/bot?botId=${segment(botId)}`,

  listBots: async () => (await request<{ bots: Bot[] }>(`${API_BASE}/bots`)).bots,
  getBot: (botId: string) => request<Bot>(`${API_BASE}/bots/${segment(botId)}`),
  deleteBot: (botId: string) =>
    request<{ ok: boolean }>(`${API_BASE}/bots/${segment(botId)}`, { method: 'DELETE' }),
  startBot: (botId: string) =>
    request<{ ok: boolean }>(`${API_BASE}/bots/${segment(botId)}/start`, { method: 'POST' }),
  stopBot: (botId: string) =>
    request<{ ok: boolean }>(`${API_BASE}/bots/${segment(botId)}/stop`, { method: 'POST' }),

  listChannels: async (botId: string) =>
    (await request<{ channels: StoredChannel[] }>(`${API_BASE}/bots/${segment(botId)}/channels`)).channels,
  revokeChannel: (botId: string, broadcasterUserId: string) =>
    request<{ ok: boolean }>(`${API_BASE}/bots/${segment(botId)}/channels/${segment(broadcasterUserId)}`, {
      method: 'DELETE',
    }),

  getSubscriptions: (botId: string, broadcasterUserId: string) =>
    request<ChannelSubscriptions>(
      `${API_BASE}/bots/${segment(botId)}/channels/${segment(broadcasterUserId)}/subscriptions`,
    ),
  setSubscriptions: (botId: string, broadcasterUserId: string, types: Array<{ type: string; version: string }>) =>
    request<ChannelSubscriptions>(
      `${API_BASE}/bots/${segment(botId)}/channels/${segment(broadcasterUserId)}/subscriptions`,
      json('PUT', { types }),
    ),
  getChannelScopes: (botId: string, broadcasterUserId: string) =>
    request<ChannelScopeStatus>(
      `${API_BASE}/bots/${segment(botId)}/channels/${segment(broadcasterUserId)}/scopes`,
    ),
  createChannelReauth: (
    botId: string,
    broadcasterUserId: string,
    input: { scopes?: string[]; ttlMs?: number } = {},
  ) =>
    request<CreatedEnrollmentLink>(
      `${API_BASE}/bots/${segment(botId)}/channels/${segment(broadcasterUserId)}/reauth`,
      json('POST', input),
    ),
  previewEnrollmentScopes: (
    botId: string,
    input: {
      types?: Array<{ type: string; version: string } | string>;
      helixEndpoints?: string[];
      features?: string[];
      extraScopes?: string[];
    },
  ) =>
    request<{ required: string[]; scopes: EnrollmentScopePreview[] }>(
      `${API_BASE}/bots/${segment(botId)}/enrollment-scope-preview`,
      json('POST', input),
    ),
  getFeatures: async (botId: string, broadcasterUserId: string) =>
    (
      await request<{ features: FeatureBinding[] }>(
        `${API_BASE}/bots/${segment(botId)}/channels/${segment(broadcasterUserId)}/features`,
      )
    ).features,
  setFeatures: (botId: string, broadcasterUserId: string, features: Array<{ featureId: string; desiredState: string }>) =>
    request<{ features: FeatureBinding[] }>(
      `${API_BASE}/bots/${segment(botId)}/channels/${segment(broadcasterUserId)}/features`,
      json('PUT', { features }),
    ),

  listEnrollments: async (botId: string) =>
    (await request<{ links: EnrollmentLink[] }>(`${API_BASE}/bots/${segment(botId)}/enrollments`)).links,
  createEnrollment: (
    botId: string,
    input: {
      scopes?: string[];
      extraScopes?: string[];
      types?: Array<{ type: string; version: string } | string>;
      helixEndpoints?: string[];
      features?: string[];
      ttlMs?: number;
      fromDesiredSubscriptions?: boolean;
      broadcasterUserId?: string;
    },
  ) => request<CreatedEnrollmentLink>(`${API_BASE}/bots/${segment(botId)}/enrollments`, json('POST', input)),
  revokeEnrollment: (linkId: string) =>
    request<{ ok: boolean }>(`${API_BASE}/enrollments/${segment(linkId)}`, { method: 'DELETE' }),

  listChannelApiKeys: async (botId: string, broadcasterUserId: string) =>
    (
      await request<{ keys: ApiKey[] }>(
        `${API_BASE}/bots/${segment(botId)}/channels/${segment(broadcasterUserId)}/apikeys`,
      )
    ).keys,
  createChannelApiKey: (
    botId: string,
    broadcasterUserId: string,
    input: { label: string; helixPermission?: 'read' | 'write'; eventTypes?: string[] },
  ) =>
    request<CreatedApiKey>(
      `${API_BASE}/bots/${segment(botId)}/channels/${segment(broadcasterUserId)}/apikeys`,
      json('POST', input),
    ),
  revokeApiKey: (keyId: string) =>
    request<{ ok: boolean }>(`${API_BASE}/apikeys/${segment(keyId)}`, { method: 'DELETE' }),

  eventSubCatalog: async () =>
    (await request<{ types: SubscriptionType[] }>(`${API_BASE}/catalog/eventsub`)).types,
  helixCatalog: async () =>
    (await request<{ endpoints: HelixEndpoint[] }>(`${API_BASE}/catalog/helix`)).endpoints,
  helixEndpoint: (endpoint: string) =>
    request<HelixEndpoint>(`${API_BASE}/catalog/helix/${segment(endpoint)}`),
  scopesCatalog: async () =>
    (await request<{ scopes: ScopeCatalogEntry[] }>(`${API_BASE}/catalog/scopes`)).scopes,
  featuresCatalog: async () =>
    (await request<{ features: FeatureBinding[] }>(`${API_BASE}/catalog/features`)).features,
  searchViewers: async (botId: string, broadcasterUserId: string, q: string, live = true) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    params.set('live', live ? '1' : '0');
    const query = params.toString();
    return (
      await request<{ viewers: ChannelViewer[] }>(
        `${API_BASE}/bots/${segment(botId)}/channels/${segment(broadcasterUserId)}/viewers${query ? `?${query}` : ''}`,
      )
    ).viewers;
  },

  listHistory: async (filters: {
    kinds?: string;
    botId?: string;
    broadcasterUserId?: string;
    limit?: number;
  } = {}) => {
    const params = new URLSearchParams();
    if (filters.kinds) params.set('kinds', filters.kinds);
    if (filters.botId) params.set('botId', filters.botId);
    if (filters.broadcasterUserId) params.set('broadcasterUserId', filters.broadcasterUserId);
    if (filters.limit != null) params.set('limit', String(filters.limit));
    const query = params.toString();
    return (await request<{ records: ActivityRecord[] }>(`${API_BASE}/history${query ? `?${query}` : ''}`)).records;
  },
  listLogs: async (filters: { botId?: string; broadcasterUserId?: string; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (filters.botId) params.set('botId', filters.botId);
    if (filters.broadcasterUserId) params.set('broadcasterUserId', filters.broadcasterUserId);
    if (filters.limit != null) params.set('limit', String(filters.limit));
    const query = params.toString();
    return (await request<{ logs: ActivityRecord[] }>(`${API_BASE}/logs${query ? `?${query}` : ''}`)).logs;
  },
  listEventsub: async (botId?: string) => {
    const query = botId ? `?botId=${segment(botId)}` : '';
    return (await request<{ subscriptions: Array<Record<string, unknown>> }>(`${API_BASE}/eventsub/subscriptions${query}`))
      .subscriptions;
  },
  reconcileEventSub: (botId?: string, broadcasterUserId?: string) => {
    const params = new URLSearchParams();
    if (botId) params.set('botId', botId);
    if (broadcasterUserId) params.set('broadcasterUserId', broadcasterUserId);
    const query = params.toString();
    return request<unknown>(`${API_BASE}/eventsub/reconcile${query ? `?${query}` : ''}`, { method: 'POST' });
  },
  callHelix: async (botId: string, broadcasterUserId: string, endpoint: string, params: Record<string, unknown>) => {
    const headers = new Headers({ 'content-type': 'application/json' });
    const response = await fetch(
      `${API_BASE}/bots/${segment(botId)}/channels/${segment(broadcasterUserId)}/helix/${segment(endpoint)}`,
      { method: 'POST', credentials: 'include', headers, body: JSON.stringify(params ?? {}) },
    );
    const text = await response.text();
    const parsed: unknown = text ? safeJson(text) : undefined;
    if (!response.ok) {
      const detail = parsed as { error?: string; message?: string; fields?: unknown; scopes?: string[] } | undefined;
      throw new ApiError(
        detail?.message || detail?.error || `${response.status} ${response.statusText}`,
        response.status,
        detail?.error ?? 'error',
        detail?.fields,
        detail?.scopes,
      );
    }
    return {
      body: parsed,
      rateLimit: {
        limit: response.headers.get('Ratelimit-Limit'),
        remaining: response.headers.get('Ratelimit-Remaining'),
        reset: response.headers.get('Ratelimit-Reset'),
      },
    };
  },
};

export type { ApiKeyScope };
